import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { finalize, forkJoin, take } from 'rxjs';
import { TicketPaymentService } from '../../../../features/tickets/services/ticket-payment.service';
import { AuthModalService } from '../../../services/auth-modal.service';
import { PurchaseStateService } from '../../../services/purchase-state.service';
import { SessionService } from '../../../services/session.service';

const ACCOUNT = {
  holder: 'Cta. Recaudadora CDM S.A.C',
  bank: 'BCP Soles',
  number: '1937335178005',
  interbank: '00219300733517800518',
} as const;

interface PaymentRecord {
  id: number;
  operationNumber: string;
  operationType: string;
  note: string;
  file: File | null;
}

@Component({
  selector: 'app-modal-ticket-transfer',
  standalone: true,
  imports: [FormsModule],
  templateUrl: './modal-ticket-transfer-natural.html',
  styleUrl: './modal-ticket-transfer-natural.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModalTicketTransferComponent {
  private readonly modal = inject(AuthModalService);
  private readonly router = inject(Router);
  private readonly session = inject(SessionService);
  private readonly ticketPayments = inject(TicketPaymentService);
  readonly purchase = inject(PurchaseStateService);
  readonly user = this.session.usuario;
  readonly account = ACCOUNT;
  readonly submitting = signal(false);
  readonly submitError = signal('');
  readonly paymentRecords = signal<PaymentRecord[]>([this.newPaymentRecord(1)]);

  close(): void {
    this.modal.close();
  }

  selectEvidence(event: Event, record: PaymentRecord): void {
    const input = event.target as HTMLInputElement;
    record.file = input.files?.[0] ?? null;
    this.paymentRecords.update(records => [...records]);
  }

  sendReceipt(event: MouseEvent): void {
    event.preventDefault();
    if (this.submitting()) return;

    const user = this.user();
    if (!user?.userId) {
      this.submitError.set('Inicia sesión para registrar la reserva.');
      return;
    }
    const userId = user.userId;
    const paymentReference = this.purchase.paymentReference();
    if (!paymentReference) {
      this.submitError.set('No encontramos la referencia de pago. Vuelve a iniciar la compra.');
      return;
    }
    const records = this.paymentRecords();
    if (records.some(record => !record.operationNumber.trim() || !record.file)) {
      this.submitError.set('Completa el código de operación y adjunta el comprobante de cada pago.');
      return;
    }

    this.submitting.set(true);
    this.submitError.set('');
    forkJoin(records.map(record => this.ticketPayments.uploadVoucher({
      paymentReference,
      userId,
      bankAccountId: 1,
      operationNumber: record.operationNumber.trim(),
      operationType: record.operationType,
      reportedAmount: this.purchase.total(),
      currencyCode: this.purchase.currencyCode(),
      note: record.note.trim(),
      file: record.file!,
    }))).pipe(
      take(1),
      finalize(() => this.submitting.set(false)),
    ).subscribe({
      next: () => {
        this.purchase.clear();
        this.modal.close();
        void this.router.navigate(['/tickets']);
      },
      error: () => this.submitError.set(
        'No pudimos registrar el comprobante. Revisa los datos e inténtalo nuevamente.',
      ),
    });
  }

  private newPaymentRecord(id: number): PaymentRecord {
    return { id, operationNumber: '', operationType: 'BANCA_MOVIL', note: '', file: null };
  }
}
